/**
 * A LINHA RECOLHIDA da rodada — marcador curto e resumo do que há dentro.
 *
 * Nasceu no redesenho da Sala do Comitê [owner, 19/09/2026]: *"Parecer
 * primeiro, rodadas recolhidas embaixo"*. Quem lê quer a conclusão; a
 * deliberação é a evidência dela, e evidência abre sob demanda.
 *
 * Mora fora de `painel-deliberacao-view.js` por duas razões: aquele arquivo já
 * estava acima do teto de 500 linhas (dívida registrada no `CLAUDE.md` do
 * módulo) e isto aqui é uma responsabilidade própria — o que a rodada FECHADA
 * promete, sem saber nada de turno, área ou parecer.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */

// O resumo por tipo de turno [owner]: `2 análises`, `1 resposta`, `1 condição`.
// A ordem é a de LEITURA do painel (parecer → réplica → condição), nunca a de
// chegada dos eventos — que é ruído de concorrência.
export const CONTAGEM_POR_TIPO = [
  { tipo: 'parecer', singular: 'análise', plural: 'análises' },
  { tipo: 'replica', singular: 'resposta', plural: 'respostas' },
  { tipo: 'condicao', singular: 'condição', plural: 'condições' },
]

// Área ainda escrevendo nesta rodada — só existe na sala AO VIVO. É CONTAGEM,
// não spinner: o indicador de movimento da rodada continua sendo um só, o do
// card que está sendo escrito dentro dela.
export const CONTAGEM_EM_CURSO = { singular: 'em curso', plural: 'em curso' }

// O que o botão da rodada PROMETE fazer. Entra no `aria-label` junto do título,
// porque o marcador (`R1`) não diz nada a quem ouve a tela.
export const ROTULO_ABRIR = 'Ver'
export const ROTULO_FECHAR = 'Ocultar'

/**
 * O nome acessível do botão da rodada — o verbo mais o que ele abre.
 *
 * @param {String} titulo Título da rodada, como a tela o mostra.
 * @param {Boolean} aberta
 * @returns {String}
 */
export function rotuloDoBotaoDaRodada(titulo, aberta) {
  return (aberta ? ROTULO_FECHAR : ROTULO_ABRIR) + ' ' + titulo
}

/**
 * O marcador curto da rodada: `R1`, `R2`, `R3`.
 *
 * Existe porque o título da rodada é uma FRASE ("Rodada 1 — cada área lê o caso
 * sozinha") e, numa pilha de linhas fechadas, frase não serve de âncora para o
 * olho. O marcador ancora; a frase segue ao lado, para quem for ler.
 *
 * @param {Number} rodada
 * @returns {String}
 */
export function marcadorDaRodada(rodada) {
  return 'R' + rodada
}

function contar(quantos, forma) {
  return quantos + ' ' + (quantos === 1 ? forma.singular : forma.plural)
}

/**
 * O resumo à direita da linha fechada: `2 análises`, `1 resposta · 1 em curso`.
 *
 * É o que a rodada recolhida PROMETE. Sem ele, fechar a rodada esconde o
 * tamanho do que está dentro e abrir vira aposta. Tipo que não ocorreu fica de
 * fora — "0 respostas" seria ruído sobre um fato que não existe.
 *
 * @param {Array} turnos Turnos desta rodada, na ordem da transcrição.
 * @param {Array} [cardsVivos] Cards da rodada ainda sendo escritos (só ao vivo).
 * @returns {String} Vazio quando a rodada está vazia — e aí não há o que abrir.
 */
export function resumoDaRodada(turnos, cardsVivos) {
  const partes = []
  for (const forma of CONTAGEM_POR_TIPO) {
    const quantos = (turnos || []).filter(function(turno) {
      return turno.tipo === forma.tipo
    }).length
    if (quantos) partes.push(contar(quantos, forma))
  }
  const emCurso = (cardsVivos || []).length
  if (emCurso) partes.push(contar(emCurso, CONTAGEM_EM_CURSO))
  return partes.join(' · ')
}
